/**
 * Centralized Authentication Logging Utility
 *
 * This module provides a unified logging interface for authentication-related
 * operations with conditional development logging to reduce noise in production.
 *
 * All logging methods automatically check NODE_ENV and only output in development
 * mode unless explicitly overridden for critical errors and warnings.
 */

/**
 * Authentication logger with conditional development logging
 */
export const authLogger = {
  /**
   * Development-only logging for debugging and troubleshooting
   * Only logs when NODE_ENV === 'development'
   *
   * @param message - The log message
   * @param args - Additional arguments to log
   */
  dev: (message: string, ...args: unknown[]): void => {
    if (process.env.NODE_ENV === 'development') {
      console.log(message, ...args);
    }
  },

  /**
   * Warning messages that should be visible in development
   * Only logs when NODE_ENV === 'development'
   *
   * @param message - The warning message
   * @param args - Additional arguments to log
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(message, ...args);
    }
  },

  /**
   * Error messages that are always logged regardless of environment
   * These represent critical issues that need attention
   *
   * @param message - The error message
   * @param args - Additional arguments to log
   */
  error: (message: string, ...args: unknown[]): void => {
    console.error(message, ...args);
  },

  /**
   * Info messages for development debugging
   * Only logs when NODE_ENV === 'development'
   *
   * @param message - The info message
   * @param args - Additional arguments to log
   */
  info: (message: string, ...args: unknown[]): void => {
    if (process.env.NODE_ENV === 'development') {
      console.info(message, ...args);
    }
  },

  /**
   * Success messages for development feedback
   * Only logs when NODE_ENV === 'development'
   *
   * @param message - The success message
   * @param args - Additional arguments to log
   */
  success: (message: string, ...args: unknown[]): void => {
    if (process.env.NODE_ENV === 'development') {
      console.log(message, ...args);
    }
  },
};
